import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Shield, Search, FileDown, EyeOff, Eraser, RefreshCw,
  AlertTriangle, Save, ChevronRight,
} from "lucide-react";

/**
 * Pagina amministrativa GDPR (chiusura-v1 - FASE B).
 * Percorso: /admin/gdpr — accesso riservato all'amministratore.
 *
 * Sezioni:
 *  1. Statistiche
 *  2. Configurazione retention
 *  3. Ricerca soggetto → azioni (export, pseudonimizzazione, anonimizzazione)
 *  4. Anteprima retention (dry-run, non distruttiva)
 */
export default function GdprPage() {
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [cfgDirty, setCfgDirty] = useState(false);
  const [cfgBusy, setCfgBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null); // 'export' | 'pseudo' | 'anon' | null
  const [preview, setPreview] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loadingRetention, setLoadingRetention] = useState(false);

  const reload = () => {
    api.get("/gdpr/stats").then(r => setStats(r.data)).catch(()=>{});
    api.get("/gdpr/config").then(r => { setCfg(r.data); setCfgDirty(false); }).catch(()=>{});
  };
  useEffect(() => { reload(); }, []);

  const doSearch = async () => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get("/subjects", { params: { q: query.trim(), limit: 20 } });
      setResults(r.data.items || r.data || []);
    } catch { setResults([]); }
    setSearching(false);
  };

  const saveCfg = async () => {
    setCfgBusy(true);
    try {
      const r = await api.put("/gdpr/config", {
        retention_years_inactive: parseInt(cfg.retention_years_inactive, 10),
        retention_years_revoked: parseInt(cfg.retention_years_revoked, 10),
        auto_apply: false,
      });
      setCfg(r.data);
      setCfgDirty(false);
      toast.success("Configurazione retention salvata.");
    } catch (ex) {
      toast.error("Errore nel salvataggio configurazione.");
    }
    setCfgBusy(false);
  };

  const loadRetention = async () => {
    setLoadingRetention(true);
    try {
      const r = await api.get("/gdpr/retention/preview");
      setRetention(r.data);
    } catch { toast.error("Errore anteprima retention."); }
    setLoadingRetention(false);
  };

  const openAction = async (subj, kind) => {
    setSelected(subj);
    setAction(kind);
    setPreview(null);
    if (kind === "pseudo") {
      try {
        const r = await api.post(`/gdpr/subjects/${subj.id}/pseudonymize-preview`);
        setPreview(r.data);
      } catch (ex) {
        toast.error(ex?.response?.data?.detail || "Errore anteprima.");
        setAction(null);
      }
    } else if (kind === "anon") {
      try {
        const r = await api.post(`/gdpr/subjects/${subj.id}/anonymize-preview`);
        setPreview(r.data);
      } catch (ex) {
        toast.error(ex?.response?.data?.detail || "Errore anteprima.");
        setAction(null);
      }
    }
  };

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <h1 className="ma-h1" data-testid="gdpr-title">
            <Shield size={22} style={{verticalAlign:"-4px", marginRight:8}}/>
            GDPR — Strumento gestionale
          </h1>
          <p className="ma-sub-desc">
            Supporto agli adempimenti di consenso, portabilità, pseudonimizzazione e anonimizzazione dei dati personali.
            Questo modulo è uno strumento gestionale, <strong>non una certificazione automatica di conformità legale</strong>.
          </p>
        </div>
        <button className="ma-btn-secondary-sm" onClick={reload} data-testid="btn-gdpr-refresh">
          <RefreshCw size={14}/> Aggiorna
        </button>
      </div>

      {/* STATISTICHE */}
      <div className="ma-form-panel" data-testid="gdpr-stats">
        <h3 className="ma-h3">Statistiche</h3>
        {!stats ? <div className="ma-loading">Caricamento…</div> : (
          <div className="ma-form-grid">
            <StatCard label="Soggetti totali" value={stats.subjects_total} testid="stat-total"/>
            <StatCard label="Con consenso attivo" value={stats.subjects_with_consent} testid="stat-consent"/>
            <StatCard label="Consenso revocato" value={stats.subjects_revoked} testid="stat-revoked"/>
            <StatCard label="Pseudonimizzati" value={stats.subjects_pseudonymized} testid="stat-pseudo"/>
            <StatCard label="Anonimizzati" value={stats.subjects_anonymized} testid="stat-anon"/>
          </div>
        )}
      </div>

      {/* CONFIGURAZIONE RETENTION */}
      <div className="ma-form-panel">
        <h3 className="ma-h3">Configurazione conservazione</h3>
        <div className="ma-hint">
          Configura i tempi di conservazione dei dati. <strong>Nessuna cancellazione automatica</strong> è attiva:
          l'anteprima ti mostrerà solo i soggetti candidati; ogni operazione va confermata manualmente.
        </div>
        {!cfg ? <div className="ma-loading">Caricamento…</div> : (
          <>
            <div className="ma-form-grid">
              <div>
                <label className="ma-label">Anni di inattività (nessuna pratica attiva)</label>
                <input type="number" min="0" max="50" className="ma-input"
                       value={cfg.retention_years_inactive}
                       data-testid="cfg-inactive"
                       onChange={e => { setCfg({...cfg, retention_years_inactive: e.target.value}); setCfgDirty(true); }}/>
              </div>
              <div>
                <label className="ma-label">Anni post-revoca consenso</label>
                <input type="number" min="0" max="50" className="ma-input"
                       value={cfg.retention_years_revoked}
                       data-testid="cfg-revoked"
                       onChange={e => { setCfg({...cfg, retention_years_revoked: e.target.value}); setCfgDirty(true); }}/>
              </div>
              <div className="ma-col-full">
                <label className="ma-checkbox">
                  <input type="checkbox" checked={false} disabled/>
                  <span>Applicazione automatica (disabilitata di default; ogni operazione richiede autorizzazione esplicita)</span>
                </label>
              </div>
            </div>
            <div className="ma-form-actions">
              <button className="ma-btn-primary" disabled={!cfgDirty || cfgBusy} onClick={saveCfg} data-testid="btn-cfg-save">
                <Save size={14}/> {cfgBusy ? "Salvataggio…" : "Salva configurazione"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ANTEPRIMA RETENTION */}
      <div className="ma-form-panel">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <h3 className="ma-h3" style={{margin:0}}>Anteprima retention (dry-run)</h3>
          <button className="ma-btn-secondary-sm" onClick={loadRetention} data-testid="btn-retention-preview">
            <RefreshCw size={14}/> {loadingRetention ? "Calcolo…" : "Calcola candidati"}
          </button>
        </div>
        {retention && (
          <>
            <p className="ma-hint">{retention.notice}</p>
            <h4 className="ma-h4">Consenso revocato oltre soglia ({retention.candidates_revoked.length})</h4>
            <RetentionTable rows={retention.candidates_revoked} kind="revoked" onAct={openAction}/>
            <h4 className="ma-h4" style={{marginTop:16}}>Soggetti inattivi oltre soglia ({retention.candidates_inactive.length})</h4>
            <RetentionTable rows={retention.candidates_inactive} kind="inactive" onAct={openAction}/>
          </>
        )}
      </div>

      {/* RICERCA SOGGETTO PER AZIONI */}
      <div className="ma-form-panel">
        <h3 className="ma-h3">Operazioni su singolo soggetto</h3>
        <p className="ma-hint">
          Cerca un cliente e scegli l'operazione: export dati, pseudonimizzazione o anonimizzazione (irreversibili).
        </p>
        <div className="ma-row" style={{gap:8}}>
          <input className="ma-input" placeholder="Nome, CF, PIVA…"
                 value={query} onChange={e => setQuery(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && doSearch()}
                 data-testid="gdpr-search"/>
          <button className="ma-btn-primary" onClick={doSearch} data-testid="btn-gdpr-search">
            <Search size={14}/> {searching ? "Ricerca…" : "Cerca"}
          </button>
        </div>
        {results.length > 0 && (
          <div className="ma-table-wrap" style={{marginTop:12}}>
            <table className="ma-table">
              <thead>
                <tr><th>Nome</th><th>Tipo</th><th>Stato</th><th>Azioni</th></tr>
              </thead>
              <tbody>
                {results.map(s => (
                  <tr key={s.id} data-testid={`gdpr-row-${s.id}`}>
                    <td><Link to={`/clients/${s.id}`} className="ma-link">{s.display_name}</Link></td>
                    <td>{s.subject_type === "company" ? "Impresa" : "Persona"}</td>
                    <td>
                      {s.is_anonymized && <span className="ma-pill pill-warn">Anonimizzato</span>}
                      {!s.is_anonymized && s.is_pseudonymized && <span className="ma-pill pill-warn">Pseudonimizzato</span>}
                      {!s.is_anonymized && !s.is_pseudonymized && <span className="ma-pill pill-ok">Attivo</span>}
                    </td>
                    <td className="ma-row-actions">
                      <button className="ma-btn-link" onClick={() => openAction(s, "export")} data-testid={`btn-export-${s.id}`}>
                        <FileDown size={12}/> Esporta
                      </button>
                      {!s.is_pseudonymized && !s.is_anonymized && (
                        <button className="ma-btn-link" onClick={() => openAction(s, "pseudo")} data-testid={`btn-pseudo-${s.id}`}>
                          <EyeOff size={12}/> Pseudonimizza
                        </button>
                      )}
                      {!s.is_anonymized && (
                        <button className="ma-btn-link ma-danger" onClick={() => openAction(s, "anon")} data-testid={`btn-anon-${s.id}`}>
                          <Eraser size={12}/> Anonimizza
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL AZIONE */}
      {action === "export" && selected && (
        <ExportModal subject={selected} onClose={() => { setAction(null); setSelected(null); }}/>
      )}
      {action === "pseudo" && selected && preview && (
        <IrreversibleActionModal
          kind="pseudonymize"
          subject={selected}
          preview={preview}
          onClose={() => { setAction(null); setSelected(null); setPreview(null); }}
          onDone={() => { setAction(null); setSelected(null); setPreview(null); reload(); if (retention) loadRetention(); }}
        />
      )}
      {action === "anon" && selected && preview && (
        <IrreversibleActionModal
          kind="anonymize"
          subject={selected}
          preview={preview}
          onClose={() => { setAction(null); setSelected(null); setPreview(null); }}
          onDone={() => { setAction(null); setSelected(null); setPreview(null); reload(); if (retention) loadRetention(); }}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, testid }) => (
  <div className="ma-info-block" data-testid={testid}>
    <div style={{fontSize:12, color:"var(--ma-text-muted)"}}>{label}</div>
    <div style={{fontSize:28, fontWeight:700}}>{value ?? "—"}</div>
  </div>
);

function RetentionTable({ rows, kind, onAct }) {
  if (!rows || rows.length === 0) return <div className="ma-empty">Nessun candidato.</div>;
  return (
    <div className="ma-table-wrap">
      <table className="ma-table">
        <thead>
          <tr>
            <th>Nome</th>
            {kind === "revoked" && <th>Revocato il</th>}
            <th>Ultimo aggiornamento</th>
            <th>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td><Link to={`/clients/${r.id}`} className="ma-link">{r.display_name}</Link></td>
              {kind === "revoked" && <td>{r.privacy_revoked_at ? new Date(r.privacy_revoked_at).toLocaleDateString("it-IT") : "—"}</td>}
              <td>{r.updated_at ? new Date(r.updated_at).toLocaleDateString("it-IT") : "—"}</td>
              <td className="ma-row-actions">
                <button className="ma-btn-link" onClick={() => onAct({id: r.id, display_name: r.display_name, is_pseudonymized: false, is_anonymized: false}, "pseudo")}>
                  Pseudonimizza <ChevronRight size={12}/>
                </button>
                <button className="ma-btn-link ma-danger" onClick={() => onAct({id: r.id, display_name: r.display_name, is_pseudonymized: false, is_anonymized: false}, "anon")}>
                  Anonimizza <ChevronRight size={12}/>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExportModal({ subject, onClose }) {
  const [busy, setBusy] = useState(false);
  const doExport = async (format) => {
    setBusy(true);
    try {
      const r = await api.get(`/gdpr/subjects/${subject.id}/export`, {
        params: { format }, responseType: "blob",
      });
      const blob = new Blob([r.data], { type: format === "zip" ? "application/zip" : "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gdpr_export_${subject.id}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Esportazione ${format.toUpperCase()} completata.`);
    } catch (ex) {
      toast.error("Errore nell'esportazione.");
    }
    setBusy(false);
  };
  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal" onClick={e => e.stopPropagation()} data-testid="modal-export">
        <div className="ma-modal-head">Esporta dati soggetto</div>
        <div className="ma-form-panel">
          <p>
            Soggetto: <strong>{subject.display_name}</strong>
          </p>
          <p className="ma-hint">
            L'export include anagrafica, pratiche, dati economici, metadati dei documenti,
            storico consenso e audit trail. I file binari dei documenti NON sono inclusi.
            L'operazione viene registrata nell'audit log.
          </p>
          <div className="ma-form-actions" style={{justifyContent:"center", gap:12}}>
            <button className="ma-btn-secondary" onClick={onClose} disabled={busy}>Annulla</button>
            <button className="ma-btn-secondary" onClick={() => doExport("json")} disabled={busy} data-testid="btn-do-export-json">
              <FileDown size={14}/> Esporta JSON
            </button>
            <button className="ma-btn-primary" onClick={() => doExport("zip")} disabled={busy} data-testid="btn-do-export-zip">
              <FileDown size={14}/> Esporta ZIP
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IrreversibleActionModal({ kind, subject, preview, onClose, onDone }) {
  const isAnon = kind === "anonymize";
  const label = isAnon ? "ANONIMIZZA" : "PSEUDONIMIZZA";
  const title = isAnon ? "Anonimizzazione irreversibile" : "Pseudonimizzazione irreversibile";
  const endpoint = isAnon ? `/gdpr/subjects/${subject.id}/anonymize`
                          : `/gdpr/subjects/${subject.id}/pseudonymize`;
  const [confirm1, setConfirm1] = useState("");
  const [confirm2, setConfirm2] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const canSubmit = confirm1 === label && confirm2 === subject.display_name && !busy;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(endpoint, {
        confirm_action: confirm1,
        confirm_subject: confirm2,
        reason: reason || null,
      });
      toast.success(isAnon ? "Soggetto anonimizzato." : "Soggetto pseudonimizzato.");
      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Errore durante l'operazione.");
    }
    setBusy(false);
  };

  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal ma-modal-rel" onClick={e => e.stopPropagation()} data-testid={`modal-${kind}`}>
        <div className="ma-modal-head">
          <AlertTriangle size={16} style={{color:"#d97706", verticalAlign:"-3px", marginRight:6}}/>
          {title}
        </div>
        <div className="ma-form-panel">
          <div className="ma-warning" style={{marginBottom:12}}>
            <AlertTriangle size={16}/>
            <span>
              <strong>Operazione IRREVERSIBILE</strong> su: <strong>{subject.display_name}</strong>.
            </span>
          </div>

          {isAnon ? (
            <>
              <h4 className="ma-h4">Effetti</h4>
              <ul>{(preview.effects || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
              <div className="ma-hint">
                Pratiche preservate: {preview.practices_preserved} —
                Documenti (metadati) preservati: {preview.documents_metadata_preserved} —
                Relazioni che saranno disattivate: {preview.relationships_deactivated}
              </div>
            </>
          ) : (
            <>
              <h4 className="ma-h4">Campi che verranno redatti</h4>
              <ul className="ma-mono" style={{fontSize:12, maxHeight:180, overflow:"auto"}}>
                {Object.entries(preview.fields_to_redact || {}).map(([k,v]) => (
                  <li key={k}><strong>{k}</strong>: {v == null ? <em>(vuoto)</em> : String(v).slice(0,80)}</li>
                ))}
              </ul>
              <div className="ma-hint">
                Pratiche preservate: {preview.practices_preserved}
              </div>
            </>
          )}

          <div className="ma-hint" style={{marginTop:12}}>{preview.notice}</div>

          <hr style={{margin:"16px 0"}}/>
          <h4 className="ma-h4">Doppia conferma</h4>
          <div className="ma-form-grid">
            <div className="ma-col-full">
              <label className="ma-label">
                1) Digita esattamente <code>{label}</code>
              </label>
              <input className="ma-input ma-mono" value={confirm1}
                     onChange={e => setConfirm1(e.target.value)}
                     data-testid={`confirm-action-${kind}`}
                     placeholder={label}/>
            </div>
            <div className="ma-col-full">
              <label className="ma-label">
                2) Digita esattamente il nome del soggetto: <code>{subject.display_name}</code>
              </label>
              <input className="ma-input" value={confirm2}
                     onChange={e => setConfirm2(e.target.value)}
                     data-testid={`confirm-subject-${kind}`}
                     placeholder={subject.display_name}/>
            </div>
            <div className="ma-col-full">
              <label className="ma-label">Motivo (facoltativo, verrà loggato nell'audit)</label>
              <input className="ma-input" value={reason} onChange={e => setReason(e.target.value)}
                     data-testid={`reason-${kind}`}
                     placeholder="Es. richiesta oblio via email del 12/02/2026"/>
            </div>
          </div>

          {err && <div className="ma-error" data-testid={`err-${kind}`}>{typeof err === "string" ? err : JSON.stringify(err)}</div>}

          <div className="ma-form-actions">
            <button className="ma-btn-secondary" onClick={onClose} disabled={busy}>Annulla</button>
            <button className={isAnon ? "ma-btn-danger" : "ma-btn-primary"}
                    onClick={submit} disabled={!canSubmit}
                    data-testid={`submit-${kind}`}>
              {busy ? "Elaborazione…" : (isAnon ? "Anonimizza definitivamente" : "Pseudonimizza definitivamente")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
